import React from 'react';
import { useLocation, useNavigate, type Location, type NavigateFunction } from 'react-router-dom';

import { rangeFromPreset, type DateRange, type PeriodPreset } from '@/nova/pages/affiliates/reports-helpers';

/**
 * Sheets kept in the address. Opening an asset's stats sheet adds `?stats=<creativeId>` to the CURRENT page's address
 * (a landing page's links sheet: `?links=<pageId>`) as a new history entry, the way the in-place asset overlay keeps
 * `?asset=` (`useAssetOverlay`). So leaving through one of the sheet's deep links and coming Back reopens the same
 * sheet, browser Back closes it, and a reload keeps it. The stats sheet's period rides along (`stats_period`).
 */

/** The stats sheet's query key: the asset's spark-creatives id. */
export const STATS_SHEET_PARAM = 'stats';
/** The stats sheet's period: a preset id (`month`, `7d`, `90d`; the default 30 days is left out) or `<from>..<to>`. */
export const STATS_PERIOD_PARAM = 'stats_period';
/** A landing page's "Links to this page" sheet: the page's spark id. */
export const LINKS_SHEET_PARAM = 'links';

export type SheetParamKey = typeof STATS_SHEET_PARAM | typeof LINKS_SHEET_PARAM;

/** The keys that go away with each sheet when it closes in place. */
const OWN_PARAMS: Record<SheetParamKey, readonly string[]> = {
  [STATS_SHEET_PARAM]: [STATS_SHEET_PARAM, STATS_PERIOD_PARAM],
  [LINKS_SHEET_PARAM]: [LINKS_SHEET_PARAM],
};

/** The default period, left out of the address. */
const DEFAULT_PRESET = '30d';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** The history state key telling an entry this sheet pushed (closing it goes back instead of adding an entry). */
const pushedKey = (param: SheetParamKey): string => `owsSheet:${param}`;

/** The search with each key set (a string) or removed (null). Pure. */
export const searchWithParams = (search: string, changes: Record<string, string | null>): string => {
  const q = new URLSearchParams(search);
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) q.delete(key);
    else q.set(key, value);
  }
  const out = q.toString();
  return out ? `?${out}` : '';
};

/**
 * Change the current address's query in place (same history entry, its state kept). Built from the LIVE address, not
 * the router's: a library swaps `/new` for the new asset's id with a raw `history.replaceState` the router does not
 * see, so `loc` (the router's location) only gives the history state here.
 */
export const replaceSearchParams = (
  navigate: NavigateFunction,
  loc: Location,
  changes: Record<string, string | null>,
): void => {
  const { pathname, search, hash } = window.location;
  navigate({ pathname, search: searchWithParams(search, changes), hash }, { replace: true, state: loc.state });
};

export interface StatsPeriod {
  preset: PeriodPreset;
  range: DateRange;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The period an address names (anything unreadable: the default 30 days). Pure but for `today`. */
export const parseStatsPeriod = (raw: string | null, today: Date): StatsPeriod => {
  if (raw === 'month' || raw === '7d' || raw === '90d') return { preset: raw, range: rangeFromPreset(raw, today) };
  const [from, to, extra] = (raw ?? '').split('..');
  if (extra === undefined && from && to && ISO_DAY.test(from) && ISO_DAY.test(to) && from <= to) {
    return { preset: 'custom', range: { from, to } };
  }
  return { preset: DEFAULT_PRESET, range: rangeFromPreset(DEFAULT_PRESET, today) };
};

/** The address value of a period (null for the default, which is left out). Pure. */
export const formatStatsPeriod = ({ preset, range }: StatsPeriod): string | null => {
  if (preset === DEFAULT_PRESET) return null;
  if (preset === 'custom') return `${range.from}..${range.to}`;
  return preset;
};

export interface SheetParam {
  /** The id the address names for this sheet, or null (closed). */
  value: string | null;
  /** Open the sheet for an id: a new history entry (Back closes it); one already open is swapped in place. */
  open: (value: string) => void;
  /** Close it: back when this sheet pushed the entry, else (a reload, a shared address) its keys are stripped in place. */
  close: () => void;
}

/** One sheet kept in the address under `param` (see the module comment). */
export const useSheetParam = (param: SheetParamKey): SheetParam => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = React.useRef(location);
  locationRef.current = location;

  // The location the sheet was closed at: it reads closed at once, before the router has answered the close (a `back`
  // lands a moment later), and any later location (a new open, Forward) reads the address again. Derived, not synced.
  const [closedAt, setClosedAt] = React.useState<Location | null>(null);
  const raw = new URLSearchParams(location.search).get(param);
  const value = raw && closedAt !== location ? raw : null;

  const open = React.useCallback(
    (next: string): void => {
      const loc = locationRef.current;
      // From the live address, not the router's (see replaceSearchParams): a just-created asset's sheet opens over
      // its id, never over `/new`.
      const { pathname, search, hash } = window.location;
      const to = { pathname, search: searchWithParams(search, { [param]: next }), hash };
      if (new URLSearchParams(loc.search).get(param)) {
        navigate(to, { replace: true, state: loc.state });
        return;
      }
      const state: Record<string, unknown> = isRecord(loc.state) ? { ...loc.state } : {};
      navigate(to, { state: { ...state, [pushedKey(param)]: true } });
    },
    [navigate, param],
  );

  const close = React.useCallback((): void => {
    const loc = locationRef.current;
    if (!new URLSearchParams(loc.search).has(param)) return;
    setClosedAt(loc);
    if (isRecord(loc.state) && loc.state[pushedKey(param)] === true) {
      navigate(-1);
      return;
    }
    const changes: Record<string, null> = {};
    for (const key of OWN_PARAMS[param]) changes[key] = null;
    replaceSearchParams(navigate, loc, changes);
  }, [navigate, param]);

  return { value, open, close };
};

export default useSheetParam;
